<script lang="ts" setup generic="TMultiple extends boolean | undefined">
import { computed, ref, watch, useTemplateRef, nextTick } from "#imports";
import { onClickOutside, useFocus } from "@vueuse/core";
import type { InputProps } from "./types";
import useValidation from "./useValidation";

// Conditional type that checks whether isMultiple is true, false or undefined
// https://stackoverflow.com/a/79173110

type TValue = undefined extends TMultiple
  ? string | null
  : TMultiple extends false
    ? string | null
    : string[];

const props = withDefaults(
  defineProps<
    InputProps & {
      options: Array<{ label: string; value: string }>;
      placeholder?: string;
      searchPlaceholder?: string;
      noResults?: string;
      isMultiple?: TMultiple;
      isSearchable?: boolean;
      isLoading?: boolean;
      isClearable?: boolean;
      isUnsetOnRemove?: boolean;
    }
  >(),
  {
    placeholder: undefined,
    searchPlaceholder: "Search...",
    noResults: "No results",
    isMultiple: undefined,
    hideLabel: false,
  },
);

const modelValue = defineModel<TValue>({ required: true });

defineSlots<{
  helpText(): void;
  displayValue(): void;
  optionLabel(props: { label: string; value: string }): void;
}>();

const input = useTemplateRef("input");

const { expose, isTouched, error } = useValidation({
  input,
  modelValue,
  props,
});
defineExpose(expose);

const searchRef = useTemplateRef("searchRef");
const { focused } = useFocus(searchRef);
const isOpen = ref<boolean>(false);

const toggleOpen = async () => {
  if (!isOpen.value) {
    isOpen.value = true;
    isTouched.value = true;
    await nextTick();
    focused.value = true;
  } else {
    focused.value = false;
    isOpen.value = false;
  }
};

const dropdownRef = useTemplateRef("dropdownRef");

onClickOutside(dropdownRef, () => {
  isOpen.value = false;
});

const selectedOptions = computed(() => {
  // regardless of isMultiple, this will return an array to simplify the logic
  const selectedArray = Array.isArray(modelValue.value)
    ? modelValue.value
    : ([modelValue.value] as string[]);
  return props.options.filter((option) => selectedArray.includes(option.value));
});

const selectedValues = computed(() =>
  selectedOptions.value.map((opt) => opt.value),
);

const toggleOption = (value: string) => {
  if (Array.isArray(modelValue.value)) {
    if (!selectedValues.value.includes(value)) {
      modelValue.value = [...modelValue.value, value] as TValue;
    } else {
      modelValue.value = selectedValues.value.filter(
        (v) => v !== value,
      ) as TValue;
    }
  } else {
    modelValue.value = value as TValue;
    isOpen.value = false;
  }
};

const displayValue = computed(() => {
  if (selectedOptions.value.length > 0) {
    if (selectedOptions.value.length === 1) {
      return selectedOptions.value[0]!.label;
    } else {
      return `${selectedOptions.value[0]!.label} (+${selectedOptions.value.length - 1} more)`;
    }
  }
  return props.placeholder ?? "\u00A0";
});

const reset = () => {
  if (Array.isArray(modelValue.value)) {
    modelValue.value = [] as string[] as TValue;
  } else {
    modelValue.value = null as TValue;
  }
  searchQuery.value = "";
};

watch(
  () => props.options,
  (newValue, oldValue) => {
    if (!props.isUnsetOnRemove) return;
    if (JSON.stringify(newValue) === JSON.stringify(oldValue)) {
      // for reasons I do not understand, this fires every time any prop changes
      return;
    }
    if (Array.isArray(modelValue.value)) {
      if (modelValue.value.length > 0) {
        modelValue.value = props.options
          .filter((opt) => (modelValue.value as string[]).includes(opt.value))
          .map((opt) => opt.value) as TValue;
      }
    } else if (modelValue.value !== null) {
      if (
        !props.options.map((o) => o.value).includes(modelValue.value as string)
      ) {
        modelValue.value = null as TValue;
      }
    }
  },
);

const searchQuery = ref<string>("");
const searchResults = computed(() => {
  if (searchQuery.value === "") {
    return props.options;
  }
  const lowercaseQuery = searchQuery.value.toLowerCase();
  return props.options.filter(
    (option) =>
      option.value.toLowerCase().includes(lowercaseQuery) ||
      option.label.toLowerCase().includes(lowercaseQuery),
  );
});
</script>

<template>
  <ClientOnly>
    <div
      :class="{ 'select-container': true, 'is-touched': isTouched }"
      ref="dropdownRef"
    >
      <label v-if="props.label" class="select-label">
        {{ props.label }}
      </label>

      <input type="text" ref="input" class="hidden" />
      <label
        class="select-trigger"
        :class="{ 'is-open': isOpen }"
        @click="toggleOpen"
      >
        <div class="select-value typeface-regular">
          <slot name="displayValue">
            {{ displayValue }}
          </slot>
        </div>

        <div class="select-indicator">
          <i class="ri-expand-up-down-line" />
        </div>
        <a
          v-if="props.isClearable && selectedOptions.length > 0"
          class="clear-button"
          @click="reset"
        >
          <i class="ri-close-circle-fill" />
        </a>
      </label>

      <div v-show="isOpen" class="select-dropdown">
        <div class="select-options typeface-regular">
          <div class="select-option select-search" v-if="props.isSearchable">
            <input
              type="text"
              ref="searchRef"
              :placeholder="props.searchPlaceholder"
              v-model="searchQuery"
            />
          </div>
          <div class="select-options-results">
            <div
              v-if="props.isLoading"
              class="select-loading-options typeface-regular"
            >
              Loading...
            </div>
            <div
              v-else
              v-for="option in searchResults"
              :key="option.value"
              :class="{
                'select-option': true,
                'is-selected': selectedValues.includes(option.value),
              }"
              @click="toggleOption(option.value)"
            >
              <slot name="optionLabel" v-bind="option">
                {{ option.label }}
              </slot>
            </div>
            <div class="select-no-options" v-if="searchResults.length === 0">
              {{ props.noResults }}
            </div>
          </div>
        </div>
      </div>
      <small v-if="$slots.helpText" class="helper-text"
        ><slot name="helpText"></slot
      ></small>
      <small v-if="isTouched && error" class="error-message">{{ error }}</small>
    </div>
  </ClientOnly>
</template>

<style lang="postcss" scoped>
@import "@/assets/css/input.pcss";
@import "@/assets/css/scrollbar.pcss";
.select-container {
  width: 100%;
  min-width: 10rem;
  position: relative;
  text-align: left;
}

.select-label {
  @mixin mp-input-label;
}

input.hidden {
  position: absolute;
  width: 0;
  height: 0;
  border: 0;
}

.select-search {
  border-bottom: 1px solid var(--color-slate-100);
}

.select-search input {
  @mixin mp-input;
  padding: 0.5rem 0.75rem;
}

.is-touched input:invalid + .select-trigger {
  @mixin mp-input-invalid;
}

.select-trigger {
  @mixin mp-input;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  padding-right: 2.75rem;
  min-height: 2.25rem;
  /* margin: 0.5rem 0; */
  position: relative;

  &.is-open {
    border-color: var(--color-indigo-500) !important;
    box-shadow: none !important;
  }
}

.select-value {
  font-family: "Hanken Grotesk", sans-serif;
  max-height: 1.2rem;
  overflow: hidden;
  text-align: left;
}

.select-indicator {
  position: absolute;
  right: 0.75rem;
  color: var(--color-slate-400);

  i {
    font-size: 1rem;
  }
}

.select-shortcuts {
  padding: 0.5rem 1rem;
  text-align: right;
  border-top: 1px solid var(--color-slate-100);
}

.clear-button {
  position: absolute;
  top: 50%;
  right: 2rem;
  transform: translateY(-50%);
  background: transparent;
  color: var(--color-slate-400);
  cursor: pointer;
  padding: 0.25rem;

  i {
    font-size: 1rem;
  }
}

.select-dropdown {
  position: absolute;
  background: white;
  @mixin mp-input;
  padding: 0;
  border-radius: 0.5rem;
  max-height: fit-content;
  min-width: fit-content;
  z-index: 9999;
  box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);
}

.select-options-results {
  background: white;
  max-height: 15rem;
  overflow-y: scroll;
  @mixin scrollbar-reset;
}

.select-option {
  padding: 0.75rem 0.75rem 0.75rem 1rem;
  cursor: pointer;

  &:hover {
    background-color: var(--color-slate-100);
  }

  &.is-selected {
    background-color: var(--color-slate-200);
  }
}

.select-no-options,
.select-loading-options {
  padding: 0.75rem 0.75rem 0.75rem 1rem;
  cursor: pointer;

  &:hover {
    background-color: var(--color-slate-100);
  }

  &.is-selected {
    background-color: var(--color-slate-200);
  }
}

.helper-text {
  @mixin mp-helper-text;
}

.error-message {
  @mixin mp-error-text;
  display: block;
}
</style>
